import { useSelector, useDispatch } from "react-redux";
import { useEffect, useMemo } from "react";
import RoleFetcher from "./FetchRolePermission";
import { setMenuAccess, removeMenuAccess } from "../Redux/actions/settingsActions";
import AccessDenied from "./AccessDenied";
import Spinner from "Common/Globle/Spinner";



const DEFAULT_PERMS = ["can_access", "can_create", "can_edit", "can_delete"];

const useRoleVerify = (page_url = "", menu_access = []) => {
  const dispatch = useDispatch();
  const menus = useSelector((state) => state.menusaccess?.menusaccess || []);
  const user = useSelector((state) => state.user);

  // Extract last segment from URL safely
  if (!page_url) {
    const url = new URL(window.location.href);
    page_url = url.pathname.split("/").filter(Boolean).pop() || "";
  };

  // Fetch roles if menus not present and not admin
  useEffect(() => {
    if (!menus?.length && user?.is_admin !== 1) {
      RoleFetcher({ dispatch, user, setMenuAccess, removeMenuAccess });
    }
  }, [menus, user, dispatch]);

  return useMemo(() => {
    // 1. Admin always has access
    if (user?.is_admin === 1) return true;

    // 2. Menus not loaded yet
    if (!menus?.length) return false;

    // 3. Find current page permission
    const permission = menus.find(
      (m) => String(m.page_url) === String(page_url)
    );

    if (!permission) return false;

    // 4. Check custom requested permissions
    if (Array.isArray(menu_access) && menu_access.length > 0) {
      return menu_access.some((perm) => Number(permission?.[perm]) === 1);
    }

    // 5. Fallback to default perms
    return DEFAULT_PERMS.some((perm) => Number(permission?.[perm]) === 1);
  }, [menus, user, page_url, menu_access]);
};

// Backward-compatible hook that also returns loading state: [allowed, loading]
const useRoleVerifyWithLoading = (page_url = "", menu_access = []) => {
  const dispatch = useDispatch();
  const menus = useSelector((state) => state.menusaccess?.menusaccess || []);
  const user = useSelector((state) => state.user);

  // Extract last segment from URL safely
  if (!page_url) {
    const url = new URL(window.location.href);
    page_url = url.pathname.split("/").filter(Boolean).pop() || "";
  }

  // Fetch roles if menus not present and not admin
  useEffect(() => {
    if (!menus?.length && user?.is_admin !== 1) {
      RoleFetcher({ dispatch, user, setMenuAccess, removeMenuAccess });
    }
  }, [menus, user, dispatch]);

  return useMemo(() => {
    const loading = !menus?.length && user?.is_admin !== 1;

    // Admin always has access
    if (user?.is_admin === 1) return [true, false];

    if (!menus?.length) return [false, true];

    const permission = menus.find((m) => String(m.page_url) === String(page_url));
    if (!permission) return [false, false];

    if (Array.isArray(menu_access) && menu_access.length > 0) {
      return [menu_access.some((perm) => Number(permission?.[perm]) === 1), loading];
    }

    return [DEFAULT_PERMS.some((perm) => Number(permission?.[perm]) === 1), loading];
  }, [menus, user, page_url, menu_access]);
};

const CanAccessThisPage = ({ menu_access = [], children, page_url }) => {
  const currentPath = window.location.pathname.replace(/^\/admin\//, "");
  const parent = currentPath.split("/")[0];
  const targetUrl = page_url || parent;

  const [allowed, loading] = useRoleVerifyWithLoading(targetUrl, menu_access);

  if (loading) {
    return <Spinner isSpinner={true} />
  }

  if (!allowed) {
    return <AccessDenied />
  }

  return children;
};

export { useRoleVerify, useRoleVerifyWithLoading, CanAccessThisPage };
